import { JobsPort } from './ports'
import {
  BehaviorSubject,
  combineLatest,
  defer,
  merge,
  Observable,
  OperatorFunction,
  Subject,
  firstValueFrom,
} from 'rxjs'
import { action, computed, observable, makeObservable } from 'mobx'
import Job from '../Job'
import {
  batchElements,
  every,
  filterNonNil,
  flowToggle,
  shareBehavior,
  unpair,
} from 'utils/rx-util'
import {
  concatMap,
  distinctUntilChanged,
  filter,
  find,
  map,
  mergeAll,
  share,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { fromStream, toStream } from 'utils/mobx-util'
import { Id } from 'types/definitions'
import { CancellationToken } from 'infra/cancellation'
import { extractMessageFromError } from 'utils/error-util'
import { DraftChange } from './DraftChange'

/**
 * Persister creates the job when we have all the necessary input
 * and updates it as things change, ensuring updates are serialized.
 *
 * NOTE: The `persist$` pipeline is subscribed to immediately since it is being
 * used with `fromStream`.
 */
export class Persister {
  /**
   * Used by `queue`.
   *
   * @private
   */
  private readonly _manuallyQueuedChanges = new Subject<DraftChange>()

  /**
   * Used to signal to the UI that the loaded Job was not a Draft.
   *
   * @private
   */
  private readonly _notADraft = new Subject<Job>()

  /**
   * Controls whether the Persister has started.
   * Until this emits `true` for the first time, changes
   * are buffered.
   *
   * @private
   */
  private readonly _running = new BehaviorSubject(false)

  /**
   * Sequenced PersistInputs.
   * @private
   */
  private readonly persistInput$ = merge(
    this.changes$,
    this._manuallyQueuedChanges
  ).pipe(
    map((change, i): PersistInput => [i + 1, this.job, change]),
    share()
  )

  /**
   * The persist pipeline.
   *
   * @private
   */
  readonly persist$ = this.persistInput$.pipe(
    sequenceAndBatchPersistOperations(this.adapter, this._running),
    share()
  )

  /**
   * Used for serializing persists and correlating persist
   * requests with responses. For example, if we queue 3 persists within
   * a short timeframe, we want all 3 to resolve once the last request finishes.
   *
   * @private
   */
  private readonly inputSeq$ = this.persistInput$.pipe(
    unpair(),
    shareBehavior(0)
  )

  /**
   * Observable that emits a `boolean` for whether or not we are currently
   * persisting.
   *
   * @private
   */
  readonly persisting$ = combineLatest([
    this.inputSeq$,
    this.persist$.pipe(
      map((x) => x.seq),
      shareBehavior(0)
    ),
  ]).pipe(
    // We are persisting if the last persist stream emission
    // seq number is lower than the last input seq.
    map(([inputSeq, lastPersistSeq]) => lastPersistSeq < inputSeq),
    distinctUntilChanged(),
    shareBehavior(false)
  )

  /**
   * Emits a value when a Job is created.
   */
  readonly jobCreated$ = this.persist$.pipe(
    // The Persist result only contains a `job` when one was just created.
    map((x) => x.job),
    filterNonNil(),
    // Set the job instance.
    tap((job) => this.set(job)),
    share()
  )

  /**
   * Emits a value if we detect that the loaded Job is not a Draft.
   * Used by the View to redirect to Job Details.
   */
  readonly notADraft$ = this._notADraft.asObservable()

  /**
   * Emits changes that were persisted.
   */
  readonly persistedChanges$ = this.persist$.pipe(
    map((x) => x.persistedChanges),
    mergeAll(),
    share()
  )

  /**
   * The Job that is being drafted.
   */
  @observable job = Job.none

  /**
   * The Job being drafted as a stream. Emits
   * immediately on subscribe, but ONLY if we have an actual
   * Job instance.
   */
  readonly job$ = toStream(() => this.job, true).pipe(
    filter((job) => job !== Job.none)
  )

  /**
   * The last persist result.
   */
  readonly lastPersistResult$ = this.persist$.pipe(
    shareBehavior(PersistResult.nothingHappened(0))
  )

  /**
   * Streams as MobX Observables.
   *
   * @private
   */
  private readonly $ = {
    persisting: fromStream(false, this.persisting$),
    lastPersistResult: fromStream(
      PersistResult.nothingHappened(0),
      this.lastPersistResult$
    ),
  }

  /**
   * Constructor.
   *
   * @param changes$ Observable that emits changes to be persisted.
   *  These get sequenced through the `sequenceAndBatchPersistOperations`
   *  operator which can then batch and serialize them as it sees fit.
   * @param adapter The adapter needed for loading and persisting the draft.
   */
  constructor(
    private changes$: Observable<DraftChange>,
    private adapter: JobsPort
  ) {
    makeObservable(this)
    this.queueChange = this.queueChange.bind(this)
    this.persist$.subscribe()
    this.changes$.subscribe()
    this.jobCreated$.subscribe()
    this.persisting$.subscribe()
  }

  /**
   * Whether or not we are currently persisting.
   */
  @computed
  get persisting() {
    return this.$.persisting()
  }

  /**
   * Whether or not we are currently persisting.
   */
  @computed
  get lastPersistResult() {
    return this.$.lastPersistResult()
  }

  /**
   * The time that the draft was last saved.
   */
  @computed
  get lastSavedAt() {
    return this.job.dateModified
  }

  /**
   * Whether the job has been created or if we can still make foundational changes to it.
   */
  @computed
  get jobWasCreated() {
    return this.job !== Job.none
  }

  /**
   * Waits for queued persists to have flushed.
   * If no persists are queued, resolves immediately.
   *
   * @param ct If cancelled, resolves immediately.
   */
  waitForFlush = async (ct = CancellationToken.default) => {
    // Wait for the latest queued persist to have been completed.
    await firstValueFrom(
      this.persisting$.pipe(
        find((v) => !v),
        takeUntil(ct.asObservable())
      ),
      { defaultValue: null }
    )
  }

  /**
   * Prepares the Persister.
   *
   * @param jobId
   * @param ct
   */
  async prepare(jobId: Id, ct = CancellationToken.default) {
    if (!jobId) {
      return
    }

    const job = await this.adapter.fetchJob(jobId)
    ct.throwIfCancelled()
    if (!job.isDraft) {
      this._notADraft.next(job)
    }
    this.set(job)
  }

  /**
   * Starts persisting; any buffered and future changes will start flowing.
   */
  start() {
    this._running.next(true)
  }

  /**
   * Queues a change and waits for the next flush.
   *
   * @param change
   * @param ct
   */
  async queueChange(
    change: DraftChange,
    ct = CancellationToken.default
  ): Promise<PersistResult> {
    this._manuallyQueuedChanges.next(change)
    await this.waitForFlush(ct)
    return this.lastPersistResult
  }

  /**
   * Sets the Job instance.
   *
   * @param job
   */
  @action.bound
  private set(job: Job) {
    this.job = job
  }
}

/**
 * Operator used for sequencing and batching persist operations.
 *
 * @param adapter The adapter for persisting
 * @param running$ Controls whether we are persisting or buffering from
 *  the outside. Used to buffer until the persister has started. Should
 *  emit immediately on subscribe.
 */
function sequenceAndBatchPersistOperations(
  adapter: JobsPort,
  running$: Observable<boolean>
): OperatorFunction<PersistInput, PersistResult> {
  // Operator that ensures persists are serialized
  // but also batched when possible.
  return (source$) =>
    defer(() => {
      // Used to control the flow.
      // Whenever we are processing a change, we stop the flow and start buffering.
      // When done, we start flowing and process the buffered inputs as a batch.
      const flowingSubject = new BehaviorSubject<boolean>(true)
      const flowing$ = every([running$, flowingSubject])
      // If drafting fails because we didn't have enough info to create
      // a draft, the changes are stashed here so we can include them
      // the next time changes flow through.
      // Example:
      //   1. [TeamSelected], not enough to create a draft on its own.
      //      ^ stashed
      //   2. [LegendSelected], combine with stash, now we
      //      have [TeamSelected, LegendSelected], that's enough
      //      and so the draft gets created and the stash is cleared.
      let stashedForNextRound: readonly DraftChange[] = []
      return source$.pipe(
        flowToggle(flowing$, true),
        // If there is a batch waiting to be flushed it will be caught
        // by `batchElements.`
        batchElements(10),
        // Once we have a batch to process,
        // stop the flow and start buffering.
        tap(() => flowingSubject.next(false)),
        // Process the batch.
        concatMap(async (batch) => {
          // Gets the job and seq from the last entry in the batch.
          const [seq, job] = batch[batch.length - 1]
          const optimized = optimizeChanges([
            ...stashedForNextRound,
            ...batch.map(([, , c]) => c),
          ])
          // Clear the stash.
          stashedForNextRound = []
          return job === Job.none
            ? // If there was no job, try to create it now.
              maybeCreateDraft(seq, optimized)
            : // Otherwise update the existing one
              persistChanges(job, seq, optimized)
        }),
        // After a batch has been processed,
        // start the flow again.
        tap(() => flowingSubject.next(true))
      )

      /**
       * Persists a batch of changes to the job.
       *
       * @param job
       * @param seq
       * @param changes
       */
      async function persistChanges(
        job: Job,
        seq: number,
        changes: DraftChange[]
      ) {
        // Persist changes to the job.
        try {
          const persistedChanges = await adapter.persistChanges(job, changes)
          return PersistResult.draftUpdated(seq, persistedChanges)
        } catch (err) {
          return PersistResult.failed(seq, err)
        }
      }

      /**
       * Create a Draft if we have collected enough input to do so.
       *
       * @param seq
       * @param changes
       */
      async function maybeCreateDraft(
        seq: number,
        changes: readonly DraftChange[]
      ): Promise<PersistResult> {
        try {
          const job = await adapter.draft(changes)
          if (!job) {
            // Not enough info to create the draft yet; stash these changes
            // so we can include them for the next round.
            stashedForNextRound = changes
            return PersistResult.nothingHappened(seq)
          }
          return PersistResult.draftCreated(seq, job, changes)
        } catch (err) {
          return PersistResult.failed(seq, err)
        }
      }
    })
}

/**
 * Optimizes the changes by removing duplicates.
 *
 * @param changes
 */
function optimizeChanges(changes: DraftChange[]): DraftChange[] {
  const optimized: DraftChange[] = []
  // Reverse loop because we only want the last entry of each type.
  for (let i = changes.length - 1; i >= 0; i--) {
    const change = changes[i]
    if (optimized.some((c) => c.type === change.type)) {
      continue
    }
    optimized.unshift(change)
  }

  return optimized
}

/**
 * Delegate for queueing a change and waiting for it to have been persisted.
 */
export type QueueChange = (
  change: DraftChange,
  ct?: CancellationToken
) => Promise<PersistResult>

/**
 * Input for the persist operator.
 */
export type PersistInput = [number, Job, DraftChange]

/**
 * Persist result.
 */
export interface PersistResult {
  /**
   * The sequence number matching the last change that was successfully persisted.
   */
  seq: number
  /**
   * If specified, sets the `Job` instance to this.
   * Used by the result of creating the Draft.
   */
  job?: Job
  /**
   * If specified, the operation failed.
   */
  errorMessage?: string
  /**
   * The changes that were persisted, if any.
   */
  persistedChanges: readonly DraftChange[]
}

/**
 * Creation function for a `PersistResult` shape.
 *
 * @param seq
 * @param job
 * @param errorMessage
 * @param persistedChanges
 */
export function PersistResult(
  seq: number,
  job?: Job,
  errorMessage?: string,
  persistedChanges: readonly DraftChange[] = []
): PersistResult {
  return { seq, job, errorMessage, persistedChanges }
}

/**
 * Nothing happened.
 *
 * @param seq
 */
PersistResult.nothingHappened = function nothingHappened(seq: number) {
  return PersistResult(seq)
}

/**
 * Job draft was created.
 *
 * @param seq
 * @param job
 * @param persistedChanges
 */
PersistResult.draftCreated = function draftCreated(
  seq: number,
  job: Job,
  persistedChanges: readonly DraftChange[]
) {
  return PersistResult(seq, job, undefined, persistedChanges)
}

/**
 * Job was updated.
 *
 * @param seq
 * @param persistedChanges
 */
PersistResult.draftUpdated = function draftUpdated(
  seq: number,
  persistedChanges: DraftChange[]
) {
  return PersistResult(seq, undefined, undefined, persistedChanges)
}

/**
 * Operation failed.
 *
 * @param seq
 * @param error
 */
PersistResult.failed = function draftUpdated(seq: number, error: unknown) {
  return PersistResult(seq, undefined, extractMessageFromError(error))
}
